import { useEffect, useMemo, useState } from 'react';
import { usePrevious } from '@core/hooks/usePrevious';
import { useContextTranslation } from '@core/i18n';
import { isLiveStream, isLiveStreamReplay } from '@core/livestream/helpers';
import { useShareLink } from '@core/sharing';
import { isMobileWeb } from '@core/userAgent';
import { MAX_HORIZONTAL_PANEL_WIDTH } from '@embed/components/VideoPlayer/Player3/components/modals/shared/constants';
import { PlayerModal } from '@embed/components/VideoPlayer/Player3/components/PlayerModal';
import { EventType, useCustomPlayerVideoEvent, } from '@embed/components/VideoPlayer/Player3/hooks/useCustomPlayerVideoEvent';
import { usePlayerLayout, VideoOrientation, } from '@embed/components/VideoPlayer/Player3/hooks/usePlayerLayout';
import { useVideoEl } from '@embed/components/VideoPlayer/Player3/hooks/useVideoEl';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { useVideoPlayerStore } from '@embed/components/VideoPlayer/store';
import { videoPlayerIsFullscreenSelector } from '@embed/components/VideoPlayer/store/selectors';
import Modal from '@player/components/Modal';
import { DrawerPosition } from '@player/components/Modal/Drawer';
import useLiveStreamTracking from '@player/features/liveStream/tracking/useLiveStreamTracking';
import { SHARE_PANEL_CLOSE_BUTTON } from '@player/testIds';
import { SharePanelOptionWrapper } from './SharePanel.styles';
import { SharePanelButton } from './SharePanelButton';
import { SharePanelTimestamp } from './SharePanelTimestamp';
/**
 *
 */
export const SharePanel = ({ onDismiss, onLinkCopy, shareMessage, isMobile = isMobileWeb(), }) => {
    const appContext = useStore((state) => state.appContext);
    const params = useStore((state) => state.params);
    const video = useStore((state) => state.video);
    const isFullscreen = useVideoPlayerStore(videoPlayerIsFullscreenSelector);
    const { videoOrientation } = usePlayerLayout();
    const videoId = video?.encoded_id;
    const videoId_previous = usePrevious(videoId);
    const videoEl = useVideoEl();
    const { t } = useContextTranslation();
    const initialTimeStamp = useMemo(() => Number(videoEl.currentTime.toFixed(0)), [videoEl]);
    const [customTimeFrameInSeconds, setCustomTimeFrameInSeconds] = useState(initialTimeStamp);
    const [isShareWithTimeStampEnabled, setIsShareWithTimeStampEnabled] = useState(false);
    const liveStreamTracking = useLiveStreamTracking();
    const customStartTime = isShareWithTimeStampEnabled
        ? customTimeFrameInSeconds
        : undefined;
    const shareUrl = useShareLink({
        video,
        appContext: appContext,
        params,
        startTime: customStartTime,
    });
    const customPlayerVideoEvent = useCustomPlayerVideoEvent();
    useEffect(() => {
        if (videoId && videoId !== videoId_previous) {
            customPlayerVideoEvent({
                type: EventType.FW_VIDEO_CLICK_SHARE,
            });
            liveStreamTracking?.trackClickShareVideo(video.encoded_id);
            if (!params?.ui_share_sheet) {
                onDismiss();
            }
        }
    }, [
        customPlayerVideoEvent,
        liveStreamTracking,
        onDismiss,
        params,
        video,
        video?.encoded_id,
        videoId,
        videoId_previous,
    ]);
    const buttonProps = {
        onDismiss,
        onLinkCopy,
        shareMessage,
        shareUrl: shareUrl ?? '',
        videoEl,
        shareSeconds: customStartTime,
    };
    const options = [
        params?.ui_share_sheet?.includes('sms') && isMobile ? (<SharePanelButton key="sms" type="sms" {...buttonProps}/>) : null,
        params?.ui_share_sheet?.includes('whatsapp') && isMobile ? (<SharePanelButton key="whatsapp" type="whatsapp" {...buttonProps}/>) : null,
        params?.ui_share_sheet?.includes('facebook') ? (<SharePanelButton key="facebook" type="facebook" {...buttonProps}/>) : null,
        params?.ui_share_sheet?.includes('twitter') ? (<SharePanelButton key="twitter" type="twitter" {...buttonProps}/>) : null,
        params?.ui_share_sheet?.includes('copy') ? (<SharePanelButton key="copy" type="copy" {...buttonProps}/>) : null,
        params?.ui_share_sheet?.includes('native') &&
            typeof window !== 'undefined' &&
            'share' in window.navigator ? (<SharePanelButton key="native" type="native" {...buttonProps}/>) : null,
    ].filter(Boolean);
    const maxWidth = isFullscreen && videoOrientation === VideoOrientation.HORIZONTAL
        ? MAX_HORIZONTAL_PANEL_WIDTH
        : undefined;
    const closeLabel = t('player:Close Share Panel');
    return options.length ? (<PlayerModal onBackdropClick={onDismiss} title={t('Share')} closeButton={<Modal.ButtonClose onClick={onDismiss} aria-label={closeLabel} data-testid={SHARE_PANEL_CLOSE_BUTTON}/>}>
      <Modal.Drawer position={DrawerPosition.BOTTOM} maxWidth={maxWidth}>
        <SharePanelOptionWrapper>
          {options}
          {!isLiveStream(video) || isLiveStreamReplay(video) ? (<SharePanelTimestamp initialTimeStamp={initialTimeStamp} isTimeStampEnabled={isShareWithTimeStampEnabled} onUpdateTimeFrameInSeconds={setCustomTimeFrameInSeconds} onToggleTimeStampEnabled={setIsShareWithTimeStampEnabled}/>) : null}
        </SharePanelOptionWrapper>
      </Modal.Drawer>
    </PlayerModal>) : null;
};
